import {action, computed, makeObservable, observable} from "mobx";
import type {
    GridAggregationModel,
    GridApi,
    GridCallbackDetails,
    GridColDef,
    GridColumnResizeParams,
    GridColumnVisibilityModel,
    GridDensity,
    GridEventListener,
    GridGroupingColDefOverride,
    GridGroupingColDefOverrideParams,
    GridPaginationModel,
    GridPinnedColumnFields,
    GridRowGroupingModel,
    GridRowId,
    GridRowModel,
    GridSortModel
} from "@mui/x-data-grid-premium";
import {GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD} from "@mui/x-data-grid-premium";
import {ensureFirstElementsInArray, ensureLastElementsInArray, getLocalStorageJson} from "../../../utils/js-utils.ts";
import {DEFAULT_PAGE_SIZE} from "../constants/Pagination.ts";
import {GeneralGroupingCell} from "../cells/GeneralGroupingCell.tsx";
import type {NestedRelatedDataModel} from "../../../services/ApiHelpers.ts";
import {flattenRelatedDataFields} from "../../../services/ApiHelpers.ts";
import type {JSONSerializableGridColDef} from "../MuiXColumnDefinitions.ts";
import type {BagStore} from "../../../stores/BagStore.tsx";

export const SETTINGS_KEY = 'column-settings'
export const VISIBILITY_KEY = 'visibility';
export const PINNING_KEY = 'pinning';
export const GROUPING_KEY = 'grouping';
export const AGGREGATION_KEY = 'aggregation';
export const ORDERING_KEY = 'order';
export const SORTING_KEY = 'sorting';
export const BASE_SETTINGS_STORAGE_KEY = 'muix-base-table-settings';

export interface BaseTableSettings {
    density?: GridDensity;
}

export const BASE_DEFAULT_SETTINGS: BaseTableSettings = {density: 'standard'}
export const GROUP_COLUMN_INTERPOLATION = '__row_group_by_columns_group_field__'
export const SUBGROUP_PAGINATION_PREPEND_ID = 'subgroup_pagination_';

export const INITIAL_PAGINATION_MODEL: GridPaginationModel = {
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
};

export type StoredColumnDefinition = Partial<Pick<GridColDef, 'width'>>;

export interface MuixColumnSettings {
    columns: Record<string, StoredColumnDefinition>;
}

export class BaseMuixTableController {
    /**
     * Important: Do not make gridApi observable
     */
    gridApi?: GridApi;
    // Base pagination
    _baseCount: number = 0;
    _basePage: number = 0;
    _basePerPage: number = DEFAULT_PAGE_SIZE;
    // grid config col
    leftPinnedCols: string[] = [GRID_CHECKBOX_SELECTION_FIELD];
    rightPinnedCols: string[] = [];
    // grid functional variables
    muixGridSortModel?: GridSortModel;
    muiXGridPinnedColumns?: GridPinnedColumnFields;
    muiXGridColumnVisibilityModel?: GridColumnVisibilityModel;
    muixGridGroupModel?: GridRowGroupingModel;
    muixGridAggregationModel?: GridAggregationModel;
    paginationModel: GridPaginationModel = INITIAL_PAGINATION_MODEL;
    // Custom variables
    autoResize = true;
    // Row state info
    refreshData = false;
    expand1stTimeToLeafCounter = 0;
    expandedRowIds: Set<GridRowId> = new Set<GridRowId>();
    paginationDisabledRowIds: Set<GridRowId> = new Set<GridRowId>();
    paginationHiddenRowIds: Set<GridRowId> = new Set<GridRowId>();
    loadingRowIds: Set<GridRowId> = new Set<GridRowId>();
    disableInteractionRowIds: Set<GridRowId> = new Set<GridRowId>();
    pageMap: Map<GridRowId, number> = new Map();
    perPageMap: Map<GridRowId, number> = new Map();

    constructor(
        protected bagStore: BagStore,
        // This prepended name would be used to store keys in local storage or log
        public storageName: string = 'BaseMuixTableController',
    ) {
        // See: https://mobx.js.org/subclassing.html
        makeObservable(this, {
            gridApi: false,
            // Base pagination
            _baseCount: observable,
            _basePage: observable,
            _basePerPage: observable,
            muixCustomizedPaginationCount: computed,
            muixCustomizedPaginationPage: computed,
            muixCustomizedPaginationRowsPerPage: computed,
            muixCustomizedPaginationOnPageChange: action,
            muixCustomizedPaginationOnRowsPerPageChange: action,
            leftPinnedCols: false,
            rightPinnedCols: false,
            tableColumns: computed,
            tableAggregationOnlyColumns: computed,
            defaultSortModel: computed,
            muixGridSortModel: observable,
            tableSettings: computed,
            onSettingsChange: action,
            sortModel: computed,
            onSortModelChange: action,
            muiXGridPinnedColumns: observable,
            pinnedColumns: computed,
            onPinnedColumnsChange: action,
            muiXGridColumnVisibilityModel: observable,
            visibilityModel: computed,
            onVisibilityModelChange: action,
            paginationModel: observable,
            onPaginationModelChange: action,
            muixGridGroupModel: observable,
            isGroupBy: computed,
            removeGroupingCol: action,
            addGroupingCol: action,
            rowGroupingModel: computed,
            onRowGroupingModelChange: action,
            groupingColDef: computed,
            muixGridAggregationModel: observable,
            aggregationModel: computed,
            setAutoResize: action,
            // Custom variables
            autoResize: observable,
            // Row state info
            refreshData: observable,
            expand1stTimeToLeafCounter: false,
            expandedRowIds: observable,
            paginationDisabledRowIds: observable,
            paginationHiddenRowIds: observable,
            loadingRowIds: observable,
            disableInteractionRowIds: observable,
            pageMap: observable,
            perPageMap: observable,
            resetRowState: action,
            // Datasource helpers
            transformGetRows: action,
            // Pagination
            updateGroupPage: action,
            updateGroupPerPage: action,
            setGroupExpansion: action,
        });
    }

    //region Base pagination
    get muixCustomizedPaginationDisabled() {
        return this._baseCount < this._basePerPage;
    }

    get muixCustomizedPaginationCount() {
        return this._baseCount;
    }

    get muixCustomizedPaginationPage() {
        return this._basePage;
    }

    get muixCustomizedPaginationRowsPerPage() {
        return this._basePerPage;
    }

    muixCustomizedPaginationOnPageChange(page: number) {
        console.debug('muixCustomizedPaginationOnPageChange', page);
        this._basePage = page;
        if (!this.gridApi) return;
        this.gridApi.dataSource.fetchRows(undefined, {
            skipCache: true,
            paginationModel: {
                page: this._basePage,
                pageSize: this._basePerPage
            }
        });
    }

    muixCustomizedPaginationOnRowsPerPageChange(rowsPerPage: number) {
        console.debug('muixCustomizedPaginationOnRowsPerPageChange', rowsPerPage);
        this._basePage = 0;
        this._basePerPage = rowsPerPage;
        if (!this.gridApi) return;
        this.gridApi.dataSource.fetchRows(undefined, {
            skipCache: true,
            paginationModel: {
                page: this._basePage,
                pageSize: this._basePerPage
            }
        });
    }

    //endregion

    // Make sure you override these
    get tableColumns(): GridColDef[] {
        return [];
    }

    get tableAggregationOnlyColumns(): JSONSerializableGridColDef[] {
        return [];
    }

    get defaultSortModel(): GridSortModel | undefined {
        return;
    }

    //region Local storage getter setters
    getColumnSettings = (): MuixColumnSettings => {
        return getLocalStorageJson(this.storageName + '-' + SETTINGS_KEY) ?? {columns: {}};
    };

    storeColumnWidthChange = (params: GridColumnResizeParams, event, details) => {
        console.debug('storeColumnWidthChange', params, event, details);
        if (this.autoResize) return;
        const settings = this.getColumnSettings();
        const {colDef, width} = params;
        const s: StoredColumnDefinition = settings.columns[colDef.field] ?? {};
        s.width = width;
        settings.columns[colDef.field] = s;
        localStorage.setItem(this.storageName + '-' + SETTINGS_KEY, JSON.stringify(settings));
    }

    storeColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
        localStorage.setItem(this.storageName + '-' + VISIBILITY_KEY, JSON.stringify(model));
    }

    getColumnVisibilityModel: () => GridColumnVisibilityModel = () => {
        return getLocalStorageJson(this.storageName + '-' + VISIBILITY_KEY) ?? {};
    }

    storeColumnPinningChange = (pinnedColumns: GridPinnedColumnFields) => {
        localStorage.setItem(this.storageName + '-' + PINNING_KEY, JSON.stringify(pinnedColumns));
    }

    getColumnPinning: () => GridPinnedColumnFields = () => {
        return getLocalStorageJson(this.storageName + '-' + PINNING_KEY) ?? {};
    }

    storeGroupingModelChange = (model: GridRowGroupingModel) => {
        localStorage.setItem(this.storageName + '-' + GROUPING_KEY, JSON.stringify(model));
    }

    getGroupingModel: () => GridRowGroupingModel = () => {
        return getLocalStorageJson(this.storageName + '-' + GROUPING_KEY) ?? [] as GridRowGroupingModel;
    }

    storeAggregationModelChange = (model: GridAggregationModel) => {
        localStorage.setItem(this.storageName + '-' + AGGREGATION_KEY, JSON.stringify(model));
    }

    getAggregationModel: () => GridAggregationModel = () => {
        return getLocalStorageJson(this.storageName + '-' + AGGREGATION_KEY) ?? {} as GridAggregationModel;
    }

    storeSortModelChange = (model: GridSortModel) => {
        localStorage.setItem(this.storageName + '-' + SORTING_KEY, JSON.stringify(model));
    }

    getSortModel: () => GridSortModel = () => {
        // Default sorting key should be same as BE's default
        return getLocalStorageJson(this.storageName + '-' + SORTING_KEY) ?? [{field: 'p_spend', sort: 'desc'}];
    }

    storeColumnOrderChange: GridEventListener<'columnOrderChange'> = (params, event, details) => {
        const order: string[] = details.api.getAllColumns().map(c => c.field);
        localStorage.setItem(this.storageName + '-' + ORDERING_KEY, JSON.stringify(order));
    }

    applyColumnOrdering<T extends GridColDef>(columns: T[]): T[] {
        const order: string[] = getLocalStorageJson(this.storageName + '-' + ORDERING_KEY) ?? [];
        if (order.length === 0) {
            if (this.bagStore.configStore.databagConfig?.columns) {
                return columns.sort((a, b) => {
                    if (!this.bagStore.configStore.getDefaultOrder(b.field)) return -1;
                    if (!this.bagStore.configStore.getDefaultOrder(a.field)) return 1;
                    return this.bagStore.configStore.getDefaultOrder(a.field)! - this.bagStore.configStore.getDefaultOrder(b.field)!;
                });
            }
            return columns;
        }
        columns.sort((a, b) => {
            const aIndex = order.indexOf(a.field);
            const bIndex = order.indexOf(b.field);
            if (aIndex === -1 && bIndex === -1) return 0; // both not in order
            if (aIndex === -1) return 1; // 'a' not in order, 'b' is
            if (bIndex === -1) return -1; // 'b' not in order, 'a' is
            return aIndex - bIndex; // both in order, sort by index
        });
        return columns;
    }

    applyColumnSettings<T extends GridColDef>(columns: T[]): T[] {
        // While autosize is on, do not pin widths from localStorage onto column defs (avoids fighting
        // `autosizeColumns` / internal dimensions). Toggling autoResize off reapplies storage via
        // `restoreColumnWidthsFromLocalStorage` so the grid state matches saved settings.
        if (this.autoResize) {
            return columns;
        }
        const settings = this.getColumnSettings();
        return columns.map(col => {
            const stored = this.rowGroupingModel.includes(col.field) ?
                settings.columns[GRID_TREE_DATA_GROUPING_FIELD] :
                settings.columns[col.field];
            if (stored) {
                if (stored.width) col.width = stored.width;
            }
            return col;
        });
    }

    /** Push widths from merged `tableColumns` + grouping column storage into the grid (after autosize). */
    restoreColumnWidthsFromLocalStorage() {
        if (!this.gridApi) return;
        const api = this.gridApi;
        for (const colDef of this.tableColumns) {
            if (colDef.width != null && api.getColumn(colDef.field)) {
                api.setColumnWidth(colDef.field, colDef.width);
            }
        }
        const groupingW = this.getColumnSettings().columns[GRID_TREE_DATA_GROUPING_FIELD]?.width;
        if (groupingW != null && api.getColumn(GRID_TREE_DATA_GROUPING_FIELD)) {
            api.setColumnWidth(GRID_TREE_DATA_GROUPING_FIELD, groupingW);
        }
    }

    devopsClearTableSettings = () => {
        localStorage.removeItem(BASE_SETTINGS_STORAGE_KEY)
    }

    devopsClearTableColumnSettings = () => {
        localStorage.removeItem(this.storageName + '-' + SETTINGS_KEY);
        localStorage.removeItem(this.storageName + '-' + VISIBILITY_KEY);
        localStorage.removeItem(this.storageName + '-' + PINNING_KEY);
        localStorage.removeItem(this.storageName + '-' + ORDERING_KEY);
        localStorage.removeItem(this.storageName + '-' + SORTING_KEY);
        localStorage.removeItem(this.storageName + '-' + GROUPING_KEY);
        localStorage.removeItem(this.storageName + '-' + AGGREGATION_KEY);
        this.resetRowState();
    }
    //endregion
    //region Datagrid state vars & event handlers  state
    get tableSettings(): BaseTableSettings {
        try {
            const storedSettings = localStorage.getItem(BASE_SETTINGS_STORAGE_KEY);
            return storedSettings ? JSON.parse(storedSettings) : BASE_DEFAULT_SETTINGS;
        } catch {
            return BASE_DEFAULT_SETTINGS;
        }
    };

    onSettingsChange(settings: BaseTableSettings) {
        localStorage.setItem(BASE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }

    get sortModel(): GridSortModel {
        return this.muixGridSortModel ?? this.getSortModel();
    }

    onSortModelChange(sortModel, _details): void {
        this.muixGridSortModel = sortModel;
        this.storeSortModelChange(sortModel);
        this.resetRowState();
    }

    get pinnedColumns(): GridPinnedColumnFields {
        const pinnedColumns = this.muiXGridPinnedColumns ?? this.getColumnPinning();
        if (this.isGroupBy && !pinnedColumns.left?.includes(GRID_TREE_DATA_GROUPING_FIELD))
            pinnedColumns.left?.push(GRID_TREE_DATA_GROUPING_FIELD)
        // TODO: As improvement we can also disable the pinning option in the menu on these columns
        // This is the only way I was able to make the SELECTION appear before the GROUPING in grouped mode...
        pinnedColumns.left = ensureFirstElementsInArray(pinnedColumns.left, this.leftPinnedCols);
        pinnedColumns.right = ensureLastElementsInArray(pinnedColumns.right, this.rightPinnedCols);
        // Note: cached functions must not change observables
        return pinnedColumns;
    }

    onPinnedColumnsChange(pinnedColumns: GridPinnedColumnFields, details: GridCallbackDetails): void {
        console.debug('onPinnedColumnsChange', details?.reason, pinnedColumns);
        this.muiXGridPinnedColumns = pinnedColumns;
        this.storeColumnPinningChange(pinnedColumns);
    }

    get visibilityModel(): GridColumnVisibilityModel {
        const model = {...(this.muiXGridColumnVisibilityModel ?? this.getColumnVisibilityModel())};
        // Note that MuiX example uses hiding column when it wants to hide grouped column (hides grouping by default)
        //  I spent ~2 Days thinking that it shouldn't even consider grouping column when it renders the table and user
        //      should not be able to make that column visible. It turned out that it does just hide the grouping column like a
        //      normal hidden column (which I didn't expect), but it works like that.
        //      See the following and also stackblitz example
        //  https://mui.com/x/react-data-grid/row-grouping/#hide-grouped-columns
        this.rowGroupingModel.forEach(field => {
            model[field] = false;
            model[GROUP_COLUMN_INTERPOLATION.replace('field', field)] = false;
        });
        model[GRID_TREE_DATA_GROUPING_FIELD] = this.isGroupBy;
        return model;
    }

    onVisibilityModelChange(model: GridColumnVisibilityModel, details: GridCallbackDetails): void {
        console.debug('onVisibilityModelChange', details, model);
        // Set default_visibility
        (this.bagStore.configStore.databagConfig?.columns ?? []).forEach(col => {
            if (!col.default_visibility && model[col.column_name] === undefined)
                delete model[col.column_name];
        })
        this.muiXGridColumnVisibilityModel = model;
        this.storeColumnVisibilityModelChange(this.muiXGridColumnVisibilityModel ?? {});
        this.resetRowState();
    }

    onPaginationModelChange(paginationModel: GridPaginationModel, details: GridCallbackDetails<'pagination'>): void {
        console.debug('onPaginationModelChange', details.reason, paginationModel);
        this.paginationModel = paginationModel;
    }

    get isGroupBy(): boolean {
        return this.rowGroupingModel.length > 0;
    }

    removeGroupingCol(field: string) {
        console.debug('removeGroupingCol', field, this.muixGridGroupModel);
        if (!this.gridApi || !this.muixGridGroupModel || this.muixGridGroupModel.findIndex(i => i === field) < 0)
            return;
        const removedIndex = this.muixGridGroupModel.findIndex(i => i === field);
        this.muixGridGroupModel.splice(removedIndex, 1);
        this.storeGroupingModelChange(this.muixGridGroupModel);
        this.resetRowState(true);
    }

    addGroupingCol(field: string) {
        console.debug('addGroupingCol', field, this.muixGridGroupModel)
        if (!this.gridApi || !this.muixGridGroupModel || this.muixGridGroupModel.findIndex(i => i === field) >= 0)
            return;
        this.muixGridGroupModel.push(field);
        this.storeGroupingModelChange(this.muixGridGroupModel);
        this.resetRowState(true);
    }

    get groupingColDef(): GridGroupingColDefOverride {
        return {
            minWidth: 300,
            ...(this.autoResize ? {maxWidth: 800} : {}),
            renderCell: (params) => <GeneralGroupingCell
                rowNode={params.rowNode}
                row={params.row}
                controller={this}
            />,
        } as GridGroupingColDefOverride;
    }

    groupingColDefCallback: ((params: GridGroupingColDefOverrideParams) => (GridGroupingColDefOverride | undefined | null)) = (params: GridGroupingColDefOverrideParams) => {
        return {} as GridGroupingColDefOverride;
    }

    get rowGroupingModel(): GridRowGroupingModel {
        return this.muixGridGroupModel ?? this.getGroupingModel();
    }

    onRowGroupingModelChange(model: GridRowGroupingModel, details: GridCallbackDetails): void {
        if (!this.gridApi) return;
        console.debug('onRowGroupingModelChange', model, details);
        this.muixGridGroupModel = model.filter(
            field => this.tableColumns.find(c => c.field === field && !!c.groupable)
        );
        this.storeGroupingModelChange(this.muixGridGroupModel);
        this.resetRowState();
    }

    get aggregationModel(): GridAggregationModel {
        return this.muixGridAggregationModel ?? this.getAggregationModel();
    }

    onAggregationModelChange(): void {
        // TODO
    }

    /**
     * Run autosize after React has committed `columns` / grouping defs and the grid has painted.
     * Calling `autosizeColumns` synchronously from `setAutoResize(true)` races MobX→React: measurements use stale
     * DOM/column state, then the next commit resets widths — flicker and wrong sizes.
     */
    scheduleAutosizeColumnsAfterPaint() {
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                if (!this.autoResize || !this.gridApi) return;
                void this.gridApi.autosizeColumns({
                    includeHeaders: true,
                    includeHeaderFilters: true,
                    includeOutliers: true,
                    disableColumnVirtualization: true
                });
            })
        );
    }

    /**
     * Hook after each `transformGetRows` batch: subscribe once to the next `rowsSet` to run autosize.
     * Override in subclasses that need autosize whenever new rows arrive (e.g. mixed fixed/autosize).
     */
    protected subscribeAutosizeAfterRowsSet(gridApi: GridApi) {
        if (!this.autoResize) return;
        const unsubscribe = gridApi.subscribeEvent('rowsSet', () => {
            unsubscribe();
            this.scheduleAutosizeColumnsAfterPaint();
        });
    }

    setAutoResize(value: boolean) {
        this.autoResize = value;
        if (this.autoResize && this.gridApi) {
            this.scheduleAutosizeColumnsAfterPaint();
        } else if (!this.autoResize && this.gridApi) {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => this.restoreColumnWidthsFromLocalStorage())
            );
        }
    }

    //endregion
    //region Datasource helpers
    transformGetRows(rows: GridRowModel[], rowCount: number, groupFields: string[], groupKeys: string[], relatedData: NestedRelatedDataModel): GridRowModel[] {
        this.refreshData = false;
        if (!this.gridApi) throw new Error('API not found, Cannot transforms rows!');
        const gridApi = this.gridApi;
        if (groupKeys.length === 0)
            this._baseCount = rowCount;
        // When a group is open, it's previous children should be moved from disabled to hidden,
        //  and newly fetched children should not be in nighter
        let addPagination = false;
        const mappedRows = rows.map(row => {
            if (groupFields.length === groupKeys.length)
                flattenRelatedDataFields(row, relatedData);
            return {...row, path: groupKeys.concat(this.dataSourceGetGroupKey(row))} as GridRowModel;
        });
        if (groupKeys.length > 0) {
            const parentRow = gridApi.getRow(groupKeys[groupKeys.length - 1]);
            gridApi.getAllRowIds().forEach(rowId => {
                const row = gridApi.getRow(rowId);
                // If row 'A' with same id exist in newly fetched rows, we must collapse both 'A' and it's parent
                if (row && row.path) {
                    if (mappedRows.some(mr => this.rowId(row) === this.rowId(mr) && (
                        row.path.length !== mr.path.length ||
                        row.path.some((pi, idx) => pi !== mr.path[idx])
                    ))) {
                        this.setGroupExpansion(this.rowId(row), false);
                        if (row.path.length > 1)
                            this.setGroupExpansion(row.path[row.path.length - 2], false);
                    }
                }
                // Set pagination hidden / disabled
                if (row &&
                    row.path.length > parentRow.path.length &&
                    row.path[parentRow.path.length - 1] === this.rowId(parentRow) &&
                    this.paginationDisabledRowIds.has(this.rowId(row))) {
                    if (!String(this.rowId(row)).startsWith(SUBGROUP_PAGINATION_PREPEND_ID) ||
                        row.path.length > parentRow.path.length + 1)
                        this.paginationHiddenRowIds.add(this.rowId(row));
                    this.paginationDisabledRowIds.delete(this.rowId(row));
                }
            });
            this.loadingRowIds.delete(this.rowId(parentRow));
            mappedRows.forEach(row => {
                this.paginationHiddenRowIds.delete(this.rowId(row));
                this.paginationDisabledRowIds.delete(this.rowId(row));
            });
            if (this.getGroupPerPage(SUBGROUP_PAGINATION_PREPEND_ID + groupKeys[groupKeys.length - 1]) <= parentRow.count) {
                addPagination = true;
                this.paginationHiddenRowIds.delete(SUBGROUP_PAGINATION_PREPEND_ID + groupKeys[groupKeys.length - 1]);
            }
        }
        mappedRows.forEach(row => {
            if (this.expandedRowIds.has(this.rowId(row)) &&
                !gridApi.getAllRowIds().find(rowId => {
                    const rowInner = gridApi.getRow(rowId);
                    return rowInner.path.length > row.path.length && row.path.every((pi, idx) => pi === rowInner.path[idx]);
                }))
                setTimeout(() => this.setGroupExpansion(this.rowId(row), true), 100);
        });
        // Expand 1'st groups to leaf
        if (this.expand1stTimeToLeafCounter < this.rowGroupingModel.length) {
            let firstRowIdWithMatchingDepth = gridApi.getAllRowIds().find(rowId => {
                if (String(rowId).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return false;
                const row = gridApi.getRow(rowId);
                return row && row.path && (row.path.length === (this.expand1stTimeToLeafCounter + 1));
            });
            if (!firstRowIdWithMatchingDepth && mappedRows.length && groupKeys.length === this.expand1stTimeToLeafCounter)
                firstRowIdWithMatchingDepth = this.rowId(mappedRows[0]);
            if (firstRowIdWithMatchingDepth) {
                this.expand1stTimeToLeafCounter += 1;
                setTimeout(() => this.setGroupExpansion(firstRowIdWithMatchingDepth!, true), 200);
            }
        }
        // Set autosize after data is loaded.
        // `rowsSet` fires when rows enter MUI X state, but React hasn't committed to the DOM yet.
        // Two nested rAFs are needed: the first yields to React's render, the second waits for the
        // browser to paint — only then does autosizeColumns have real cell dimensions to measure.
        // Debug alternative (fragile — races against render time): setTimeout(() => this.setAutoResize(true), 50)
        // Suggested by Claude Sonnet 4.6
        this.subscribeAutosizeAfterRowsSet(gridApi);
        return [...mappedRows, ...(addPagination ? [{
            id: SUBGROUP_PAGINATION_PREPEND_ID + groupKeys[groupKeys.length - 1],
            path: groupKeys.concat(SUBGROUP_PAGINATION_PREPEND_ID + groupKeys[groupKeys.length - 1]),
            subGroupPagination: true,
        }] : [])];
    }

    rowId(row: GridRowModel): GridRowId {
        return String(row.id);
    }

    dataSourceGetGroupKey(row: GridRowModel): string {
        return String(this.rowId(row));
    }

    getParentPath(groupKeys: string[]): GridRowId[] {
        console.debug('getParentPath', groupKeys);
        if (!this.gridApi) return [];
        const gridApi = this.gridApi;
        return gridApi.getRow(groupKeys[groupKeys.length - 1])?.path ?? [];
    }

    //endregion
    //region Sub-group pagination & expansion
    getSubGroupPaginationId(id: GridRowId) {
        return String(id).substring(SUBGROUP_PAGINATION_PREPEND_ID.length);
    }

    updateGroupPage(id: GridRowId, value: number) {
        if (!this.gridApi || !String(id).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return;
        id = this.getSubGroupPaginationId(id);
        const gridApi = this.gridApi;
        const parentRow = gridApi.getRow(id);
        // Note that skipCache param in fetchRows func will not work here (IDK why), we have to clear cache explicitly
        gridApi.dataSource.cache.clear();
        // Removing rows that are out of current subgroup page, would cause error regarding selected row not existing
        // Therefore here we just hide row (out of DOM, but in grid rows
        // Disadvantage of this approach is possible high memory usage
        gridApi.getAllRowIds().forEach(rowId => {
            const row = gridApi.getRow(rowId);
            if (row && rowId === id) {
                this.loadingRowIds.add(rowId);
                this.pageMap.set(rowId, value);
            }
            if (row && (row.path.length > parentRow.path.length) &&
                parentRow.path.every((pi, idx) => row.path[idx] === pi))
                this.paginationDisabledRowIds.add(this.rowId(row));
        });
        // Note that promise of fetchRows does not wait for request to finish, therefore setting disabled hidden,
        //  has no effect here
        gridApi.dataSource.fetchRows(id, {keepChildrenExpanded: true});
    }

    getGroupPage(id: GridRowId) {
        if (!this.gridApi || !String(id).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return 0;
        id = this.getSubGroupPaginationId(id);
        return this.pageMap.get(id) ?? 0;
    }

    updateGroupPerPage(id: GridRowId, value: number) {
        if (!this.gridApi || !String(id).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return;
        id = this.getSubGroupPaginationId(id);
        const gridApi = this.gridApi;
        const parentRow = gridApi.getRow(id);
        gridApi.dataSource.cache.clear();
        gridApi.getAllRowIds().forEach(rowId => {
            const row = gridApi.getRow(rowId);
            if (row && rowId === id) {
                this.loadingRowIds.add(rowId);
                this.pageMap.set(rowId, 0);
                this.perPageMap.set(rowId, value);
            }
            if (row && (row.path.length > parentRow.path.length) &&
                parentRow.path.every((pi, idx) => row.path[idx] === pi))
                this.paginationDisabledRowIds.add(this.rowId(row));
        });
        gridApi.dataSource.fetchRows(id, {keepChildrenExpanded: true});
    }

    getGroupPerPage(id: GridRowId) {
        if (!this.gridApi || !String(id).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return 0;
        id = this.getSubGroupPaginationId(id);
        return this.perPageMap.get(id) ?? DEFAULT_PAGE_SIZE;
    }

    getGroupPageCount(id: GridRowId) {
        if (!this.gridApi || !String(id).startsWith(SUBGROUP_PAGINATION_PREPEND_ID)) return 0;
        id = this.getSubGroupPaginationId(id);
        return this.gridApi.getRow(id)?.count ?? 0;
    }

    getGroupPaginationDisabled(id: GridRowId) {
        return this.paginationDisabledRowIds.has(String(id)) || this.loadingRowIds.has(this.getSubGroupPaginationId(id));
    }

    setGroupExpansion(id: GridRowId, expand: boolean) {
        console.debug('setGroupExpansion', id, expand)
        // From https://mui.com/x/react-data-grid/row-grouping/#group-expansion:
        // The apiRef.current.setRowChildrenExpansion() method is not compatible with server-side tree data or
        //  server-side row grouping. Use apiRef.current.dataSource.fetchRows() instead.
        if (!this.gridApi) return;
        const gridApi = this.gridApi;
        const parentRow = gridApi.getRow(id);
        if (!parentRow || gridApi.getRowNode(id)?.type !== 'group') {
            console.warn('setGroupExpansion: Can not proceed: ', id, expand)
            return;
        }
        if (expand) {
            gridApi.dataSource.cache.clear();
            this.loadingRowIds.add(id);
            this.expandedRowIds.add(id);
            gridApi.dataSource.fetchRows(id, {keepChildrenExpanded: true});
        } else {
            this.expandedRowIds.delete(id);
            gridApi.setRowChildrenExpansion(id, false);
        }
    }

    //endregion

    resetRowState(reloadData = false) {
        this.expandedRowIds.clear();
        this.paginationDisabledRowIds.clear();
        this.paginationHiddenRowIds.clear();
        this.loadingRowIds.clear();
        this.disableInteractionRowIds.clear();
        this.pageMap.clear();
        this.perPageMap.clear();
        this._basePage = 0;
        if (!this.gridApi) return;
        this.gridApi.getAllRowIds().forEach(rowId => {
            if (String(rowId).startsWith(SUBGROUP_PAGINATION_PREPEND_ID))
                this.paginationHiddenRowIds.add(String(rowId));
        });
        if (!reloadData) {
            this.gridApi.getAllRowIds().forEach(rowId => {
                const row = this.gridApi!.getRow(rowId);
                if (row.path && row.path.length > 1)
                    this.paginationHiddenRowIds.add(this.rowId(row));
            });
            return;
        }
        this.refreshData = true;
    }
}
